import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { berlinDate, isRealDate, monthOf, type Appointment } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { HistoryNote } from '../../components/HistoryNote';
import { PersonPicker } from '../../components/person-picker/PersonPicker';
import { useSession } from '../../session';
import { useAssignees } from './assignees';
import { useAppointmentTypes } from './appointmentTypes';
import './calendar.css';

const BACK = '/kalender/termine';

/** Eine Stunde später, als Text ohne Zeitzone gerechnet („JJJJ-MM-TTTHH:MM“). Bei der Eingabe zählt die Uhrzeit auf der Wand, nicht der Zeitpunkt, deshalb ist das hier richtig. */
function plusHour(wall: string): string {
  const d = new Date(`${wall}:00Z`);
  return Number.isNaN(d.getTime()) ? wall : new Date(d.getTime() + 3_600_000).toISOString().slice(0, 16);
}

/** Termin anlegen oder bearbeiten. Ganztägige Termine haben nur Daten, die anderen Datum und Uhrzeit. Teilnehmer sind Mitarbeiter des Betriebs. */
export function AppointmentFormPage() {
  const { id } = useParams();
  const isCreate = !id;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { can, session } = useSession();
  // Das Büro ändert alle Termine, ein Mitarbeiter nur die, die er selbst angelegt hat (KON-088), andere Termine sieht er nur
  const manager = can('calendar.write');
  const assignees = useAssignees();
  const types = useAppointmentTypes();

  const existing = useQuery({ queryKey: ['appointment', id], queryFn: async () => (await api<{ data: Appointment }>('GET', `/appointments/${id}`)).data, enabled: !isCreate });

  // Beim Anlegen ist der Tag vorbelegt, den man im Kalender gewählt hat, von 09:00 bis 10:00 Uhr
  const presetDay = params.get('day');
  const day = presetDay && isRealDate(presetDay) ? presetDay : berlinDate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [start, setStart] = useState(`${day}T09:00`);
  const [end, setEnd] = useState(`${day}T10:00`);
  const [typeId, setTypeId] = useState('');
  // Wer nicht alle Termine verwalten darf, ist der einzige Teilnehmer seiner eigenen Termine
  const [participantUserIds, setParticipantUserIds] = useState<string[]>(!manager && session ? [session.user.id] : []);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const loaded = useRef(false);
  const canWrite = isCreate || (existing.data?.editable ?? false);

  useEffect(() => {
    const a = existing.data;
    if (loaded.current || !a) return;
    loaded.current = true;
    setTitle(a.title);
    setDescription(a.description ?? '');
    setLocation(a.location ?? '');
    setAllDay(a.allDay);
    setStart(a.start);
    setEnd(a.end);
    setTypeId(a.typeId ?? '');
    setParticipantUserIds(a.participantUserIds);
  }, [existing.data]);

  const changeAllDay = (value: boolean) => {
    setAllDay(value);
    // Beim Umschalten bleibt der Tag, die Uhrzeit kommt dazu oder fällt weg
    if (value) {
      setStart(start.slice(0, 10));
      setEnd(end.slice(0, 10));
    } else {
      setStart(`${start.slice(0, 10)}T09:00`);
      setEnd(`${end.slice(0, 10)}T10:00`);
    }
  };
  // Verschiebt man den Beginn hinter das Ende, rückt das Ende mit
  const changeStart = (value: string) => {
    setStart(value);
    if (!value) return;
    if (allDay) {
      if (end < value) setEnd(value);
    } else if (end <= value) {
      setEnd(plusHour(value));
    }
  };

  const save = useMutation({
    mutationFn: () => {
      const body = { title: title.trim(), description: description.trim(), location: location.trim(), allDay, start, end, participantUserIds: manager ? participantUserIds : session ? [session.user.id] : [] };
      // Beim Anlegen bleibt ohne Art das Feld weg, beim Ändern entfernt null sie
      return isCreate
        ? api<{ data: Appointment }>('POST', '/appointments', { ...body, ...(typeId ? { typeId } : {}) })
        : api<{ data: Appointment }>('PATCH', `/appointments/${id}`, { ...body, typeId: typeId || null });
    },
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['appointments'] });
      client.removeQueries({ queryKey: ['appointment', id] });
      navigate(`${BACK}?month=${monthOf(res.data.start)}&day=${res.data.start.slice(0, 10)}`, { replace: true });
    },
    onError: (e) => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (e instanceof ApiError) {
        setErrors(e.status === 422 ? e.fields : {});
        setFormError(e.status === 422 ? 'Bitte prüfen Sie Ihre Eingaben.' : e.message);
      } else {
        setFormError('Der Termin konnte nicht gespeichert werden.');
      }
    },
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/appointments/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['appointments'] });
      client.removeQueries({ queryKey: ['appointment', id] });
      navigate(BACK, { replace: true });
    },
    onError: (e) => setFormError(e instanceof ApiError ? e.message : 'Der Termin konnte nicht gelöscht werden.'),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    setFormError(null);
    save.mutate();
  }

  if (!isCreate && existing.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!isCreate && (existing.isError || !existing.data)) {
    return (
      <div className="card narrow">
        <h1>Termin nicht gefunden</h1>
        <Link to={BACK}>Zum Kalender</Link>
      </div>
    );
  }

  const error = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);
  const inputType = allDay ? 'date' : 'datetime-local';

  return (
    <>
      <p><Link to={BACK} className="back-link">← Kalender</Link></p>
      <div className="page-head">
        <h1>{isCreate ? 'Neuer Termin' : existing.data!.title}</h1>
        {!isCreate && !canWrite && <span className="badge">Nur ansehen</span>}
        {!isCreate && <HistoryNote verb="Erstellt" stamp={existing.data!.history?.created} />}
      </div>

      <form className="card" onSubmit={submit} noValidate>
        {formError && <div className="alert alert--error" role="alert">{formError}</div>}
        <label className="field">
          <span>Titel <abbr title="Pflichtfeld">*</abbr></span>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} autoComplete="off" disabled={!canWrite} aria-invalid={!!errors.title} />
          {error('title')}
        </label>

        <label className="check">
          <input type="checkbox" checked={allDay} disabled={!canWrite} onChange={(e) => changeAllDay(e.target.checked)} />
          <span>Ganztägig</span>
        </label>

        <div className="form-grid">
          <label className="field">
            <span>Beginn <abbr title="Pflichtfeld">*</abbr></span>
            <input className="input" type={inputType} value={start} onChange={(e) => changeStart(e.target.value)} disabled={!canWrite} aria-invalid={!!errors.start} />
            {error('start')}
          </label>
          <label className="field">
            <span>{allDay ? 'Letzter Tag' : 'Ende'} <abbr title="Pflichtfeld">*</abbr></span>
            <input className="input" type={inputType} value={end} min={start} onChange={(e) => setEnd(e.target.value)} disabled={!canWrite} aria-invalid={!!errors.end} />
            {error('end')}
          </label>
          <label className="field">
            <span>Ort</span>
            <input className="input" value={location} onChange={(e) => setLocation(e.target.value)} autoComplete="off" disabled={!canWrite} aria-invalid={!!errors.location} />
            {error('location')}
          </label>
          <label className="field">
            <span>Art</span>
            <select className="input" value={typeId} onChange={(e) => setTypeId(e.target.value)} disabled={!canWrite} aria-invalid={!!errors.typeId}>
              <option value="">Keine Art</option>
              {types.list.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              {/* Eine Art, die inzwischen fehlt, bleibt gewählt, bis jemand eine andere wählt */}
              {typeId && !types.find(typeId) && <option value={typeId}>Nicht mehr verfügbar</option>}
            </select>
            {error('typeId')}
            {types.list.length === 0 && (
              <small className="muted">
                Noch keine Terminarten angelegt. {can('setup.read') ? <Link to="/einrichtung/terminarten">In der Einrichtung anlegen</Link> : 'Der Inhaber legt sie in der Einrichtung an.'}
              </small>
            )}
          </label>
        </div>

        <PersonPicker
          label="Teilnehmer"
          options={assignees.list}
          value={participantUserIds}
          onChange={setParticipantUserIds}
          disabled={!canWrite || !manager}
          selfId={session?.user.id}
          error={errors.participantUserIds}
          hint={manager ? 'Teilnehmer sehen den Termin in ihrem Kalender. Bearbeiten darf ihn das Büro und wer ihn angelegt hat.' : 'Sie tragen den Termin für sich selbst ein. Termine für andere legt das Büro an.'}
        />

        <label className="field">
          <span>Beschreibung</span>
          <textarea className="input textarea" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={5000} disabled={!canWrite} />
          {error('description')}
        </label>

        <div className="form-actions">
          {canWrite && (
            <button className="btn btn--primary" type="submit" disabled={save.isPending}>
              {save.isPending ? 'Wird gespeichert …' : isCreate ? 'Termin anlegen' : 'Speichern'}
            </button>
          )}
          <Link className="btn btn--secondary" to={BACK}>{canWrite ? 'Abbrechen' : 'Zurück'}</Link>
        </div>
        {!isCreate && <p className="history-line"><HistoryNote verb="Zuletzt bearbeitet" stamp={existing.data!.history?.updated} /></p>}
      </form>

      {!isCreate && canWrite && (can('calendar.delete') || !manager) && (
        <div className="danger-zone">
          <button
            className="btn btn--danger btn--sm"
            type="button"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`Termin „${existing.data!.title}“ wirklich löschen?`)) remove.mutate();
            }}
          >
            Termin löschen
          </button>
        </div>
      )}
    </>
  );
}
